import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
// import { featureConfig } from "./features";

dayjs.extend(duration);

export function convertToTitleCase(inputString) {
  return inputString
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function debounce(func, wait, immediate) {
  let timeout;

  return function () {
    const context = this;
    const args = arguments;

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    }, wait);

    if (immediate && !timeout) func.apply(context, args);
  };
}

export function hasPermission(userPermissions, requiredPermissions) {
  let hasPermission = false;
  const permissionObj = userPermissions.reduce((prev, curr) => {
    prev[curr] = true;
    return prev;
  }, {});

  requiredPermissions.forEach((element) => {
    if (permissionObj[element]) {
      hasPermission = true;
    }
  });

  return hasPermission;
}

export function isModuleVisible(moduleName) {
  if (typeof window === "undefined") return true;
  try {
    const orgData = JSON.parse(localStorage.getItem("orgData"));
    if (!orgData || !orgData.plan_details) {
      return true;
    }
    const planDetails = orgData.plan_details;
    return planDetails.MODULES[moduleName]?.enable === true;
  } catch (error) {
    console.error("Error parsing orgData from localStorage:", error);
    return false;
  }
}

export function isModuleFeatureAllowed(moduleName, featureName) {
  if (typeof window === "undefined") return true;
  try {
    const orgData = JSON.parse(localStorage.getItem("orgData"));
    if (!orgData || !orgData.plan_details) {
      return true;
    }
    const planDetails = orgData.plan_details;
    return (
      planDetails.MODULES[moduleName]?.features?.[featureName] === "allow" ||
      planDetails.FEATURE_LIST_AND_SETTINGS[moduleName]?.features?.[
        featureName
      ] === "allow"
    );
  } catch (error) {
    console.log(error);
    return false;
  }
}

export function isAnyFeatureAllowed(moduleName) {
  if (typeof window === "undefined") return false;

  try {
    const orgData = JSON.parse(localStorage.getItem("orgData"));
    if (!orgData || !orgData.plan_details) {
      return true;
    }

    const planDetails = orgData.plan_details;
    const module = planDetails.FEATURE_LIST_AND_SETTINGS[moduleName];

    if (module && module.features) {
      return Object.values(module.features).some(
        (feature) => feature === "allow"
      );
    }
    return false;
  } catch (error) {
    console.error("Error checking module features:", error);
    return false;
  }
}

const XLSX = require("xlsx");

export function downloadExcel(data, sheetName, fileName) {
  const excelSheet = XLSX.utils.json_to_sheet(data);
  const newBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(newBook, excelSheet, sheetName);
  XLSX.writeFile(newBook, fileName);
}

const specialOrg = ["DEVORG", "MITTAL", "GTNODE", "FRONTM", "ICNPTR"];

export function checkOrgCode(orgCode) {
  return specialOrg.includes(orgCode);
}

export function accessByString(o, s) {
  s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
  s = s.replace(/^\./, ""); // strip a leading dot
  var a = s.split(".");
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i];
    if (k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
}

export function sortDropdownOptions(options, fieldName = "text") {
  const sorted = options.sort((a, b) => {
    if (!accessByString(a, fieldName)) return 0;

    const aTxt = accessByString(a, fieldName).toLowerCase();
    const bTxt = accessByString(b, fieldName).toLowerCase();
    if (aTxt < bTxt) {
      return -1;
    }
    if (aTxt > bTxt) {
      return 1;
    }
    return 0;
  });

  return sorted;
}

export function extractData(data) {
  if (data) {
    let newData;

    Object.keys(data).forEach((year) => {
      Object.keys(data[year]).forEach((month) => {
        Object.keys(data[year][month]).forEach((day) => {
          const dataPerDay = data[year][month][day];
          newData = {
            year,
            month,
            day,
            locations: dataPerDay,
          };
        });
      });
    });

    return newData;
  } else {
    return null;
  }
}

export function extractMonthData(data) {
  if (data) {
    let newData;

    Object.keys(data).forEach((year) => {
      Object.keys(data[year]).forEach((month) => {
        const dataPerDay = data[year][month];
        newData = {
          year,
          month,

          locations: dataPerDay,
        };
      });
    });

    return newData;
  } else {
    return null;
  }
}

export function getFullAddress(address) {
  if (address) {
    const { addressLine1, addressLine2, city, country, pinCode, state } =
      address;

    if (addressLine1 == "N/A") {
      return "N/A";
    }
    const addressParts = [
      addressLine1,
      addressLine2,
      city,
      country,
      pinCode,
      state,
    ].filter((part) => part && part !== "N/A");

    return addressParts.length > 0 ? addressParts.join(", ") : "";
  }
  return "—";
}

export function sortPairDistances(pairDistance) {
  if (pairDistance) {
    const distanceBetweenPoints = pairDistance.sort((a, b) => {
      const keyA = Object.keys(a)[0];
      const keyB = Object.keys(b)[0];

      const pairNumberA = parseInt(keyA.split("_")[1]);
      const pairNumberB = parseInt(keyB.split("_")[1]);

      if (pairNumberA !== pairNumberB) {
        return pairNumberA - pairNumberB;
      } else {
        const valueA = parseFloat(Object.values(a)[0]);
        const valueB = parseFloat(Object.values(b)[0]);
        return valueA - valueB;
      }
    });
    return distanceBetweenPoints;
  }
}

export function fullName(employee) {
  if (employee) {
    const firstName = employee.firstName || "";
    const lastName = employee.lastName || "";
    return firstName + " " + lastName;
  }
  return "-";
}

export function dayHourFormat(startDate, endDate) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const diff = dayjs.duration(end.diff(start));

  const days = diff.days();
  const hours = diff.hours();
  const minutes = diff.minutes();

  if (!days) {
    return `${hours}h ${minutes}m`;
  }
  return `${days}d ${hours}h ${minutes}m`;
}

export function isValidHttpUrl(string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export const convertNumberToAmount = (num) => {
  if (!num) return "00.00";
  let decimal = "00";

  if (num % 1 !== 0) {
    [num, decimal] = String(num).split(".");
  }

  return `${num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${decimal}`;
};

export const mergeOptions = (arr) => {
  return arr.reduce((merged, item) => {
    if (item.options && Array.isArray(item.options)) {
      merged.push(...item.options);
    }
    return merged;
  }, []);
};

export const mergeOptionsDetails = (arr) => {
  return arr.reduce((merged, item) => {
    if (item.optionsList && Array.isArray(item.optionsList)) {
      merged.push(...item.optionsList);
    }
    return merged;
  }, []);
};

export const extractExcelHeaders = (selectedFile) => {
  return new Promise((resolve, reject) => {
    if (!selectedFile || selectedFile.length === 0) {
      console.error("No file selected.");
      return reject(new Error("No file selected"));
    }

    const file = selectedFile[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonSheet = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonSheet.length > 0) {
          const headers = jsonSheet[0];
          resolve(headers);
        } else {
          reject(new Error("No data found in the sheet."));
        }
      } catch (error) {
        console.error("Error processing Excel file:", error);
        reject(error);
      }
    };

    reader.onerror = (error) => {
      console.error("Error reading the file:", error);
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};
